'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function DudakDolgusuSonrasiAlkolTuketimi() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/dudak-dolgusu-sonrasi-alkol-tuketimi-bilimsel-etkiler-klinik-bulgular-ve-estetik-sonuclar';
  const pageTitle = 'Dudak Dolgusu Sonrası Alkol Tüketimi: Bilimsel Etkiler, Klinik Bulgular ve Estetik Sonuçlar';
  const pageDescription = 'Dudak dolgusu sonrası alkol neden önerilmez? Hyaluronik asit dengesi, damar geçirgenliği, morarma ve şişlik üzerindeki bilimsel etkiler.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-rose-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Dudak Dolgusu Sonrası Alkol Tüketimi', href: '/tr/blog/dudak-dolgusu-sonrasi-alkol-tuketimi-bilimsel-etkiler-klinik-bulgular-ve-estetik-sonuclar' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Dudak dolgusu sonrası alkol tüketimi ve etkileri"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-pink-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Dudak Estetiği
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Dudak Dolgusu Sonrası Alkol Tüketimi: Bilimsel Etkiler, Klinik Bulgular ve Estetik Sonuçlar
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>1 Mart 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>7 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Dudak dolgusu, yüze hacim, simetri ve estetik denge kazandıran en sık tercih edilen medikal işlemlerden biridir.
                Ancak uygulama kadar sonrası dönemde gösterilen özen de estetik sonucu belirler.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Hastaların en sık sorduğu sorulardan biri:
              </p>

              <div className="bg-pink-50 border-l-4 border-pink-400 p-4 mb-6">
                <p className="text-pink-800 font-medium text-lg">
                  "Dudak dolgusu yaptırdım, akşam bir kadeh şarap içsem olur mu?"
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <p className="text-red-800 font-medium">
                  Kısa cevap: Hayır, en az 48 saat beklenmelidir.
                </p>
                <p className="text-red-800">
                  Çünkü alkol, cilt altı dokuda ödem, morarma ve dolgu migrasyonu riskini artırır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣ Dudak Dolgusu Nasıl Çalışır?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Modern dudak dolgularının çoğu hyaluronik asit (HA) temellidir.
                Bu madde ciltte doğal olarak bulunur, su tutma kapasitesi sayesinde hacim ve nem kazandırır.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Enjeksiyon sonrası HA molekülleri dokuyla etkileşerek suyu bağlar ve "dolgun" görünüm oluşturur.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <p className="text-blue-800 mb-4">
                  📍 Ancak bu denge, ilk 48 saatte çok hassastır.
                </p>
                <p className="text-blue-800">
                  Dolgu dokuyla bütünleşmeden önce çevresel etkenlerden (ısı, alkol, nikotin, kafein) kolayca etkilenir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2️⃣ Alkolün Vücut Üzerindeki Biyolojik Etkileri
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-pink-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Etki</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Sonuç</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Vazodilatasyon (damar genişlemesi)</td>
                      <td className="border border-gray-300 px-4 py-3">Damarlar genişler, cilt altına fazla kan akışı olur</td>
                      <td className="border border-gray-300 px-4 py-3">Morarma riski artar</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Vazopermeabilite artışı</td>
                      <td className="border border-gray-300 px-4 py-3">Damar geçirgenliği artar</td>
                      <td className="border border-gray-300 px-4 py-3">Ödem (şişlik) oluşur</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sıvı-elektrolit dengesi bozulur</td>
                      <td className="border border-gray-300 px-4 py-3">Hücre dışı sıvı birikir</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu hacmi dengesizleşir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Alkol metabolitleri (asetaldehit)</td>
                      <td className="border border-gray-300 px-4 py-3">Hücre yenilenmesini yavaşlatır</td>
                      <td className="border border-gray-300 px-4 py-3">İyileşme süresi uzar</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Karaciğer yükü</td>
                      <td className="border border-gray-300 px-4 py-3">HA metabolizmasını etkiler</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu tutunması azalır</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  💬 Sonuç: Alkol, dudak dolgusu sonrası "istenen dengeyi" bozarak hem görünümü hem konforu etkiler.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3️⃣ Neden 48 Saat Beklenmeli?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dudak bölgesi, kan damarları açısından zengindir.
                Enjeksiyon sonrası dokuda mikroskobik travma oluşur.
              </p>

              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <p className="text-red-800 mb-4 font-medium">
                  Bu bölge toparlanmadan alkol alınırsa:
                </p>
                <ul className="list-disc list-inside space-y-2 text-red-800">
                  <li>Damarlar genişler → morarma artar</li>
                  <li>Su tutulumu olur → dudaklar aşırı şişer</li>
                  <li>Dolgu partikülleri yer değiştirir → asimetri oluşabilir</li>
                </ul>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                İlk 2 gün dokunun toparlanması, 3.–4. günde ise dolgunun stabilizasyonu gerçekleşir.
                Bu nedenle alkol yasağı en az 48 saat, tercihen 72 saat sürmelidir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4️⃣ Alkolün Dolgu Üzerindeki Estetik Etkileri
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Etki</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Gözle Görülür Sonuç</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Süre</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hafif morarma</td>
                      <td className="border border-gray-300 px-4 py-3">Makyajla kapatılabilir</td>
                      <td className="border border-gray-300 px-4 py-3">2–3 gün</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Şişlik</td>
                      <td className="border border-gray-300 px-4 py-3">Simetriyi bozar, "yamuk dolgu" hissi yaratır</td>
                      <td className="border border-gray-300 px-4 py-3">2–4 gün</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Dolgu migrasyonu</td>
                      <td className="border border-gray-300 px-4 py-3">Alt veya üst dudakta dengesizlik</td>
                      <td className="border border-gray-300 px-4 py-3">1–2 hafta</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Doku hassasiyeti</td>
                      <td className="border border-gray-300 px-4 py-3">Ağrı, dokunma hassasiyeti</td>
                      <td className="border border-gray-300 px-4 py-3">1–3 gün</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu bulgular genellikle geçicidir, ancak hasta konforunu ve fotoğrafik sonucu etkiler.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5️⃣ Dudak Dolgusunun İlk 48 Saatlik Fizyolojisi
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-green-50 rounded-xl p-6">
                  <h4 className="font-semibold text-green-900 mb-4">İlk 6 saat</h4>
                  <p className="text-green-800">Damar geçirgenliği en yüksek, dolgu dağılgan.</p>
                </div>
                
                <div className="bg-yellow-50 rounded-xl p-6">
                  <h4 className="font-semibold text-yellow-900 mb-4">6–24 saat</h4>
                  <p className="text-yellow-800">Dokular ödemlidir, su tutulumu maksimum.</p>
                </div>
                
                <div className="bg-blue-50 rounded-xl p-6">
                  <h4 className="font-semibold text-blue-900 mb-4">24–48 saat</h4>
                  <p className="text-blue-800">Dolgu dokuyla bütünleşmeye başlar.</p>
                </div>
                
                <div className="bg-purple-50 rounded-xl p-6">
                  <h4 className="font-semibold text-purple-900 mb-4">48 saat sonrası</h4>
                  <p className="text-purple-800">Stabilizasyon dönemi.</p>
                </div>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu yüzden erken dönemdeki her dış etken (alkol, sıcak, egzersiz) bu süreci olumsuz etkiler.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6️⃣ Klinik Gözlemlerden Örnek
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Bir hastam işlemden birkaç saat sonra kokteyl içtiğini söyledi.
                  Ertesi gün dudakta belirgin şişlik oluştu, asimetri hissi verdi.
                  72 saat sonra normale döndü ama sonuç tam oturması 10 gün sürdü."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7️⃣ Alkol ile Etkileşen Diğer Faktörler
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-orange-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Faktör</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Etkileşim</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kafein</td>
                      <td className="border border-gray-300 px-4 py-3">Damar genişletici etkiyi artırır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sigara</td>
                      <td className="border border-gray-300 px-4 py-3">Doku oksijenlenmesini azaltır</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sıcak duş / sauna</td>
                      <td className="border border-gray-300 px-4 py-3">Ödemi artırır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Aspirin / kan sulandırıcılar</td>
                      <td className="border border-gray-300 px-4 py-3">Morarma riskini katlar</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu nedenlerle dolgu sonrası dönemde tam bir vasküler denge korunmalıdır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8️⃣ Estetik Sonuç Açısından Risk
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dudak dolgusu sadece hacim değil, simetri sanatıdır.
                Erken alkol alımı, dudakların alt ve üst oranını geçici olarak değiştirir.
                Bu da "dolgu yamuk oldu" hissi yaratır.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Aslında dolgu değil, ödemin yarattığı hacim farkıdır.
                Ancak görsel olarak rahatsız edicidir ve sosyal açıdan stres yaratabilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                9️⃣ Dudak Dolgusu Sonrası Doğal Süreç
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-pink-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Gün</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Ne Olur</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Öneri</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">1. gün</td>
                      <td className="border border-gray-300 px-4 py-3">Hafif ödem, hassasiyet</td>
                      <td className="border border-gray-300 px-4 py-3">Soğuk kompres, su içmek</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">2. gün</td>
                      <td className="border border-gray-300 px-4 py-3">Şişlik azalmaya başlar</td>
                      <td className="border border-gray-300 px-4 py-3">Alkol hâlâ yasak</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">3. gün</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu yerleşir</td>
                      <td className="border border-gray-300 px-4 py-3">Hafif masaj başlanabilir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">5. gün</td>
                      <td className="border border-gray-300 px-4 py-3">Nihai form görünür</td>
                      <td className="border border-gray-300 px-4 py-3">Makyaj serbest</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                🔟 Uzman Görüşü
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Dudak dolgusu sonrası alkol, ödemi artırarak dolgunun homojenliğini bozabilir.
                  Estetik sonuçlarda en önemli dönem ilk 48 saattir.
                  Sabırla beklemek, hem simetriyi hem de kalıcılığı garantiler."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                🔬 Sonuç ve Klinik Öneri
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dudak dolgusu sonrası alkol alınması:
              </p>

              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-red-800">
                  <li>İyileşmeyi geciktirir,</li>
                  <li>Morarma ve şişliği artırır,</li>
                  <li>Dolgu homojenliğini bozabilir.</li>
                </ul>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                İlk 48 saat alkol, sauna, sigara ve yoğun egzersizden uzak durmak gerekir.
                Bu süre dolgunun dokuyla tam birleşmesini sağlar.
              </p>

              <div className="bg-pink-50 border-l-4 border-pink-400 p-4 mb-6">
                <p className="text-pink-800 font-medium">
                  💬 "Güzel dudak sabır ister; sabır, dudak çizgisini belirler."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Dolgudan sonra bira içtim, ne yapmalıyım?</h3>
                  <p className="text-gray-600">Bol su içip soğuk kompres uygulayın, gerekirse doktorunuza bilgi verin.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Ne kadar sonra alkol içebilirim?</h3>
                  <p className="text-gray-600">En az 48 saat, ideal olarak 72 saat sonra.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Alkol kalıcılığı etkiler mi?</h3>
                  <p className="text-gray-600">Evet, dolgu daha hızlı dağılabilir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Şişliği azaltmak için ne yapılmalı?</h3>
                  <p className="text-gray-600">Soğuk kompres, başı yüksek tutmak, tuzlu gıdalardan kaçınmak.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Dudak dolgusu sonrası ne yemeli?</h3>
                  <p className="text-gray-600">Yumuşak gıdalar, bol su, antioksidan meyveler önerilir.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Dudak dolgusu sonrası spor yapılabilir mi?</h3>
                  <p className="text-gray-600">İlk 24 saat ağır spor yasak, hafif yürüyüş yapılabilir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>ISAPS Global Report (2024) – "Dermal Filler Post-Care Protocols"</li>
                  <li>Harvard Health Publishing (2023) – "Alcohol's Effect on Skin Healing"</li>
                  <li>Teoxane Clinical Data (2024) – "Hyaluronic Acid Behavior in Microcirculation"</li>
                  <li>Türk Estetik Tıp Derneği (TETD) – "Dudak Dolgu Sonrası Bakım Rehberi"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#dudakdolgu</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#alkol</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#hyaluronikasit</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#ödem</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#morarma</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#DrBarışÇin</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-pink-600 to-rose-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Dudak Dolgusu Uygulaması ve Bakım Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan dudak dolgusu uygulaması ve sonrası bakım hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Dudak dolgusu uygulaması ve sonrası bakım hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-pink-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/dudak-dolgusu-teknikleri-dogal-simetrik-ve-estetik-sonuclar"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dudak Dolgusu Teknikleri</h4>
              <p className="text-gray-600 text-sm">Russian Lips, Paris Lips, Volume Lips gibi tekniklerin farkları ve uygulama süreci.</p>
            </a>
            <a
              href="/tr/blog/en-iyi-dudak-dolgusu-markasi-dogallik-kalicilik-ve-guven"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">En İyi Dudak Dolgusu Markası</h4>
              <p className="text-gray-600 text-sm">Kalıcılığı, doğallığı, FDA onayı ve güvenilirliğiyle öne çıkan dudak dolgularının karşılaştırması.</p>
            </a>
            <a
              href="/tr/blog/botoks-sonrasi-alkol-tuketimi-bilimsel-aciklamalar-klinik-etkiler-ve-uzman-onerileri"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Sonrası Alkol Tüketimi</h4>
              <p className="text-gray-600 text-sm">Botoks sonrası alkol içmek neden önerilmez? Bilimsel açıklamalar ve uzman önerileri.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-pink-600 text-white rounded-full hover:bg-pink-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
